import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Gift, Sparkles, Tag, Percent, DollarSign } from 'lucide-react';
import { API_URLS, API_CONFIG } from '../config/api';

const CouponWheelPopup = () => {
  const [coupons, setCoupons] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [canSpin, setCanSpin] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [wheelEnabled, setWheelEnabled] = useState(true);
  const wheelRef = useRef(null);

  useEffect(() => {
    const initialize = async () => {
      await checkWheelEnabled();
      await checkAndShowPopup();
    };
    initialize();
    
    // Global event listener - admin sayfasından manuel açma için
    const handleOpenWheel = async () => {
      // localStorage'ı temizle
      localStorage.removeItem('coupon_wheel_spun');
      
      // Önce çarkıfelek ayarlarını kontrol et
      try {
          const settingsResponse = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getCouponWheelSettings`);
          if (settingsResponse.ok) {
            const settingsData = await settingsResponse.json();
            // Response format: {status: 'success', is_enabled: 1} veya {status: 'error', is_enabled: 1}
            const enabled = settingsData.is_enabled === 1 || settingsData.is_enabled === true || settingsData.is_enabled === '1';
            setWheelEnabled(enabled);
            
            if (!enabled) {
              // console.log('Çarkıfelek kapalı, popup gösterilmiyor.');
              return;
            }
          }
      } catch (error) {
        // console.error('Error checking wheel settings:', error);
      }
      
      // Aktif kuponları yükle ve direkt göster
      try {
        const response = await fetch(API_URLS.ADMIN.COUPONS);
        if (response.ok) {
          const data = await response.json();
          const activeCoupons = (data.coupons || []).filter(
            c => c.is_active === 1 || c.is_active === true
          );
          
          if (activeCoupons.length > 0) {
            activeCoupons.sort((a, b) => a.id - b.id);
            setCoupons(activeCoupons);
            // Hemen göster
            setIsVisible(true);
          } else {
            // console.log('Aktif kupon bulunamadı, popup gösterilmiyor.');
          }
        }
      } catch (error) {
        // console.error('Error fetching coupons:', error);
      }
    };
    
    window.addEventListener('openCouponWheel', handleOpenWheel);
    
    return () => {
      window.removeEventListener('openCouponWheel', handleOpenWheel);
    };
  }, []);

  const checkWheelEnabled = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getCouponWheelSettings`);
      if (response.ok) {
        const data = await response.json();
        // Response format: {status: 'success', is_enabled: 1} veya {status: 'error', is_enabled: 1}
        const enabled = data.is_enabled === 1 || data.is_enabled === true || data.is_enabled === '1';
        setWheelEnabled(enabled);
      } else {
        // Hata durumunda varsayılan olarak aktif
        setWheelEnabled(true);
      }
    } catch (error) {
      // console.error('Error checking wheel enabled:', error);
      // Hata durumunda varsayılan olarak aktif
      setWheelEnabled(true);
    }
  };

  const checkAndShowPopup = async () => {
    // Her zaman backend'den wheelEnabled'ı kontrol et (state henüz güncellenmiş olmayabilir)
    let enabled = false;
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getCouponWheelSettings`);
      if (response.ok) {
        const data = await response.json();
        // Response format: {status: 'success', is_enabled: 1} veya {status: 'error', is_enabled: 1}
        enabled = data.is_enabled === 1 || data.is_enabled === true || data.is_enabled === '1';
        setWheelEnabled(enabled);
      } else {
        // Hata durumunda varsayılan olarak aktif
        enabled = true;
        setWheelEnabled(true);
      }
    } catch (error) {
      // console.error('Error checking wheel enabled:', error);
      // Hata durumunda varsayılan olarak aktif
      enabled = true;
      setWheelEnabled(true);
    }
    
    // Çarkıfelek aktif değilse gösterme
    if (!enabled) {
      // console.log('Çarkıfelek kapalı, popup gösterilmiyor.');
      return;
    }

    // Kullanıcı daha önce çarkı çevirdi mi kontrol et
    const hasSpun = localStorage.getItem('coupon_wheel_spun');
    if (hasSpun === 'true') {
      return; // Daha önce çevirdiyse gösterme
    }

    // Aktif kupon var mı kontrol et
    try {
      const response = await fetch(API_URLS.ADMIN.COUPONS);
      if (response.ok) {
        const data = await response.json();
        const activeCoupons = (data.coupons || []).filter(
          c => c.is_active === 1 || c.is_active === true
        );
        
        // Backend ile aynı sırada tutmak için ID'ye göre sırala
        activeCoupons.sort((a, b) => a.id - b.id);
        
        if (activeCoupons.length > 0) {
          setCoupons(activeCoupons);
          // Sayfa yüklendikten 2 saniye sonra göster (veya hemen göster)
          setTimeout(() => {
            setIsVisible(true);
          }, 2000);
        }
      }
    } catch (error) {
      // console.error('Error fetching coupons:', error);
    }
  };

  const checkSpinAvailability = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.CHECK_SPIN_AVAILABILITY);
      if (response.ok) {
        const data = await response.json();
        // Backend'den gelen canSpin değerini kullan
        setCanSpin(data.canSpin === true);
        
        // Eğer çevrilemiyorsa, localStorage'ı da güncelle
        if (!data.canSpin) {
          localStorage.setItem('coupon_wheel_spun', 'true');
        }
      } else {
        // Hata durumunda da çevrilebilir yap
        setCanSpin(true);
      }
    } catch (error) {
      // console.error('Error checking spin availability:', error);
      setCanSpin(true);
    }
  };

  const spinWheel = async () => {
    if (spinning || !canSpin || coupons.length === 0) return;

    setSpinning(true);
    setResult(null);

    try {
      // Animasyonu rastgele bir yere gönder (hedef segment yok)
      const spins = 5 + Math.random() * 3; // 5-8 tur
      const segmentAngle = 360 / coupons.length;
      
      // Rastgele bir ek açı ekle (0-360 arası)
      const randomExtraAngle = Math.random() * 360;
      
      // Mevcut rotasyonu al (mod 360)
      const currentRotation = rotation % 360;
      // Hedef rotasyon: mevcut + tur sayısı + rastgele açı
      const finalRotation = currentRotation + (360 * spins) + randomExtraAngle;

      // Çarkıfelekteki tüm segment'leri yazdır
      // console.log('Çarkıfelek segment\'leri:', coupons.map((c, idx) => ({
      //   index: idx,
      //   couponId: c.id,
      //   value: c.discount_value,
      //   type: c.discount_type,
      //   display: c.discount_type === 'percentage' 
      //     ? `%${parseFloat(c.discount_value).toFixed(0)}`
      //     : `${parseFloat(c.discount_value).toFixed(0)}₺`
      // })));

      // console.log('Çarkıfelek animasyon başlatılıyor:', {
      //   spins,
      //   randomExtraAngle,
      //   segmentAngle,
      //   finalRotation,
      //   currentRotation
      // });

      setRotation(finalRotation);

      // Animasyon bitince durduğu segment'i hesapla
      setTimeout(async () => {
        try {
          // Final rotasyon değerini al (mod 360)
          // Not: rotation state'i animasyon sırasında güncelleniyor, bu yüzden finalRotation'ı direkt kullanıyoruz
          const actualFinalRotation = finalRotation % 360;
          
          // Segment açıları +90'dan başlıyor (üstte)
          // İşaretçi 0 derecede (üstte)
          // Rotasyon saat yönünün tersine dönüyor (pozitif = saat yönünün tersine)
          // Çarkıfelek döndüğünde, segment'ler de dönüyor
          // İşaretçi sabit (0 derece), çarkıfelek dönüyor
          
          // Rotasyon değerine göre hangi segment işaretçide olduğunu bul
          // Segment açıları +90'dan başlıyor, bu yüzden:
          // Segment 0: 90 ile (90 + segmentAngle) arası, ortası = 90 + segmentAngle/2
          // Rotasyon X ise, segment'ler -X dereceye kaydı (saat yönünün tersine döndüğü için)
          // İşaretçi 0 derecede, bu yüzden: hangi segment'in ortası 0 derecede?
          // 90 + N*segmentAngle + segmentAngle/2 - X = 0
          // N*segmentAngle + segmentAngle/2 = X - 90
          // N = (X - 90 - segmentAngle/2) / segmentAngle
          
          // Rotasyon değerine göre segment açısını hesapla
          // Rotasyon saat yönünün tersine dönüyor
          // İşaretçi 0 derecede (üstte), segment açıları +90'dan başlıyor
          // Çarkıfelek X derece döndüyse, segment'ler -X dereceye kaydı
          // İşaretçi hangi segment'i gösteriyor?
          // Segment N'nin ortası: 90 + N*segmentAngle + segmentAngle/2
          // Rotasyon X ise: 90 + N*segmentAngle + segmentAngle/2 - X = 0
          // N*segmentAngle + segmentAngle/2 = X - 90
          // N = (X - 90 - segmentAngle/2) / segmentAngle
          
          // Daha basit: actualFinalRotation değerine göre segment açısını hesapla
          // Rotasyon saat yönünün tersine, bu yüzden segment açısı = (270 - actualFinalRotation) mod 360
          // (270 = 90 + 180, yani 180 derece kaydırılmış)
          let segmentAngleFromTop = (270 - actualFinalRotation) % 360;
          if (segmentAngleFromTop < 0) segmentAngleFromTop += 360;
          
          // segmentAngleFromTop değeri 90'dan başlıyor (segment açıları +90'dan başlıyor)
          // Bu yüzden önce 90'ı çıkarıp sonra segmentAngle'a bölmeliyiz
          let adjustedAngle = segmentAngleFromTop - 90;
          if (adjustedAngle < 0) adjustedAngle += 360;
          
          const segmentIndex = Math.floor(adjustedAngle / segmentAngle);
          const clampedSegmentIndex = Math.min(Math.max(segmentIndex, 0), coupons.length - 1);
          
          // console.log('Segment hesaplama detayları:', {
          //   actualFinalRotation,
          //   segmentAngleFromTop,
          //   adjustedAngle,
          //   segmentAngle,
          //   segmentIndex,
          //   clampedSegmentIndex
          // });
          
          // Durduğu segment'teki kuponu al
          const wonCoupon = coupons[clampedSegmentIndex];
          
          // console.log('Çarkıfelek durdu, hesaplama:', {
          //   actualFinalRotation,
          //   segmentAngleFromTop,
          //   segmentIndex,
          //   clampedSegmentIndex,
          //   wonSegmentValue: wonCoupon.discount_type === 'percentage'
          //     ? `%${parseFloat(wonCoupon.discount_value).toFixed(0)}`
          //     : `${parseFloat(wonCoupon.discount_value).toFixed(0)}₺`,
          //   couponId: wonCoupon.id,
          //   couponValue: wonCoupon.discount_value,
          //   couponType: wonCoupon.discount_type
          // });

          // Backend'e kupon kullanımını kaydet
          const response = await fetch(API_URLS.ADMIN.SPIN_WHEEL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ coupon_id: wonCoupon.id })
          });

          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success') {
              setResult(wonCoupon);
              // Çarkı çevrildi, localStorage'a kaydet (backend session zaten kaydetti)
              localStorage.setItem('coupon_wheel_spun', 'true');
              setCanSpin(false); // Artık çevrilemez
            } else {
              alert(data.message || 'Kupon kaydedilirken bir hata oluştu.');
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            alert(errorData.message || 'Kupon kaydedilirken bir hata oluştu.');
          }
        } catch (error) {
          // console.error('Error saving coupon:', error);
          alert('Kupon kaydedilirken bir hata oluştu.');
        } finally {
          setSpinning(false);
          setCanSpin(true);
        }
      }, 3000);
    } catch (error) {
      // console.error('Error spinning wheel:', error);
      setSpinning(false);
      alert('Çarkıfelek çevrilirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Kullanıcı popup'ı kapattı, bir daha gösterme
    localStorage.setItem('coupon_wheel_spun', 'true');
  };

  if (!isVisible || !wheelEnabled) return null;

  const segmentAngle = coupons.length > 0 ? 360 / coupons.length : 360 / 8;
  const colors = [
    '#FF1744', '#00E676', '#00B0FF', '#FF6D00', '#1DE9B6',
    '#FFEA00', '#D500F9', '#2979FF', '#FFC400', '#00C853',
    '#E91E63', '#00BCD4', '#FF9800', '#9C27B0', '#2196F3'
  ];

  // SVG çarkıfelek oluştur
  const renderWheel = () => {
    if (coupons.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full">
          <p className="text-white font-bold">Kupon Yok</p>
        </div>
      );
    }

    const radius = 120;
    const centerX = 128;
    const centerY = 128;

    return (
      <svg width="256" height="256" viewBox="0 0 256 256" className="w-full h-full">
        {coupons.map((coupon, index) => {
          // Segment açıları +90'dan başlıyor (üstte)
          const startAngle = (index * segmentAngle + 90) * (Math.PI / 180);
          const endAngle = ((index + 1) * segmentAngle + 90) * (Math.PI / 180);
          
          const x1 = centerX + radius * Math.cos(startAngle);
          const y1 = centerY + radius * Math.sin(startAngle);
          const x2 = centerX + radius * Math.cos(endAngle);
          const y2 = centerY + radius * Math.sin(endAngle);
          
          const largeArc = segmentAngle > 180 ? 1 : 0;
          
          const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ');

          const color = colors[index % colors.length];
          const textAngle = (index * segmentAngle + segmentAngle / 2 + 90) * (Math.PI / 180);
          const textRadius = radius * 0.7;
          const textX = centerX + textRadius * Math.cos(textAngle);
          const textY = centerY + textRadius * Math.sin(textAngle);

          return (
            <g key={coupon.id}>
              <path
                d={pathData}
                fill={color}
                stroke="#fff"
                strokeWidth="2"
              />
              <text
                x={textX}
                y={textY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="14"
                fontWeight="bold"
                transform={`rotate(${index * segmentAngle + segmentAngle / 2 + 90}, ${textX}, ${textY})`}
              >
                {coupon.discount_type === 'percentage' 
                  ? `%${parseFloat(coupon.discount_value).toFixed(0)}`
                  : `${parseFloat(coupon.discount_value).toFixed(0)}₺`
                }
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md relative overflow-hidden animate-in fade-in zoom-in duration-300">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-10"
          onClick={handleClose}
        >
          <X className="w-5 h-5" />
        </Button>

        <CardContent className="p-6">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-8 h-8 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">Şans Çarkı</h2>
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <p className="text-gray-600">Çarkı çevir, indirim kuponu kazan!</p>
          </div>

          {/* Çarkıfelek */}
          <div className="relative mb-6 flex items-center justify-center">
            <div className="relative w-64 h-64">
              {/* Çarkıfelek Container */}
              <div
                ref={wheelRef}
                className="relative w-full h-full rounded-full border-8 border-gray-200 overflow-hidden shadow-lg"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: spinning ? 'transform 3s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
                }}
              >
                {renderWheel()}
              </div>

              {/* İşaretçi */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 z-10">
                <div className="w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-t-[30px] border-t-purple-600"></div>
              </div>
            </div>
          </div>

          {/* Sonuç */}
          {result && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Gift className="w-6 h-6 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">Tebrikler!</h3>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">{result.title}</p>
                <Badge variant="outline" className="font-mono text-lg px-4 py-2 mb-2">
                  {result.code}
                </Badge>
                <div className="flex items-center justify-center gap-2 mt-2">
                  {result.discount_type === 'percentage' ? (
                    <>
                      <Percent className="w-5 h-5 text-purple-600" />
                      <span className="text-2xl font-bold text-purple-600">
                        %{parseFloat(result.discount_value).toFixed(0)}
                      </span>
                      <span className="text-gray-600">İndirim</span>
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-5 h-5 text-purple-600" />
                      <span className="text-2xl font-bold text-purple-600">
                        {parseFloat(result.discount_value).toFixed(2)} ₺
                      </span>
                      <span className="text-gray-600">İndirim</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Buton */}
          <Button
            onClick={spinWheel}
            disabled={spinning || !canSpin || coupons.length === 0}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            size="lg"
          >
            {spinning ? (
              <>
                <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                Çevriliyor...
              </>
            ) : !canSpin ? (
              <>
                <Tag className="w-5 h-5 mr-2" />
                Bugün Zaten Çevirdiniz
              </>
            ) : coupons.length === 0 ? (
              'Kupon bulunamadı'
            ) : (
              <>
                <Tag className="w-5 h-5 mr-2" />
                Çarkı Çevir!
              </>
            )}
          </Button>

          {!canSpin && !result && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-center text-sm text-blue-700">
                🎉 Bugün çarkı zaten çevirdiniz! Yarın tekrar deneyebilirsiniz.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CouponWheelPopup;

